import { Pub, Sub, Service } from '../../../shared/services/service.abstract.ts';
import { Observable, switchMap, map, delay, repeat, takeWhile, filter, take } from 'rxjs';
import {
  InitProdStatisticCalcMutationVariables,
  GetProdStatisticDataQuery,
  InitProdStatisticCalcMutation,
  C_Statistic_Calc_Status,
} from '../../../graphql/generatedModel.ts';
import { gqlClient } from '../../../graphql/graphqlRequest.ts';
import { initProdStatisticCalc, getProdStatisticData } from './gql/productionStatistic.gql.ts';
import {
  STATISTIC_MAX_POLLING_ATTEMPTS,
  takeFinishedStatisticData,
} from './statisticJob.helper.ts';

type Actions = 'getProductionStatisticData' | undefined;

class PubImpl extends Pub<Actions> {
  getProductionStatisticData(params: InitProdStatisticCalcMutationVariables): void {
    this.emit('getProductionStatisticData', params);
  }
}

class SubImpl extends Sub<Actions> {
  getProductionStatisticData(): Observable<GetProductionStatisticDataRes> {
    return this.actionListener('getProductionStatisticData').pipe(
      switchMap(({ params }) => {
        return gqlClient(
          initProdStatisticCalc,
          params,
        ) as Observable<InitProdStatisticCalcMutation>;
      }),
      delay(1300),
      switchMap((res) => {
        const jobId = res?.wawiAssist?.initWA_ProdStatisticCalc.jobId;
        return (
          gqlClient(getProdStatisticData, { jobId }) as Observable<GetProdStatisticDataQuery>
        ).pipe(
          repeat({ delay: 1000 }),
          map((data) => {
            return data.wawiAssist?.getWA_statisticData;
          }),
          takeWhile((data) => {
            return data?.status === C_Statistic_Calc_Status.SCS_LOADING;
          }, true),
          filter((data, index) => {
            const isInProgress = data?.status === C_Statistic_Calc_Status.SCS_LOADING;
            const maxAttemptsReached = index + 1 > STATISTIC_MAX_POLLING_ATTEMPTS;
            return !isInProgress || maxAttemptsReached;
          }),
          take(1),
        );
      }),
      map((res) => {
        // Previously only the still-loading case threw (a bare `new Error()`), while SCS_ERROR
        // resolved to `null` and crashed the state on `{ gridData } = null` (WA-3887).
        return takeFinishedStatisticData(
          res,
          'Production statistic',
        ) as GetProductionStatisticDataRes;
      }),
    );
  }
}

class ProductionStatisticService extends Service<Actions> {
  pub = new PubImpl(this.stream$);
  sub = new SubImpl(this.stream$);
}

export const productionStatisticService = new ProductionStatisticService();

export type GetProductionStatisticDataRowRes = NonNullable<
  NonNullable<GetProdStatisticDataQuery['wawiAssist']>['getWA_statisticData']
>;
export type GetProductionStatisticDataRes = Extract<
  NonNullable<GetProductionStatisticDataRowRes['data']>,
  { __typename?: 'WA_ProductionStatisticData' }
>;
